import { useEffect } from 'react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { FormRow, RadioField, Text, TextField, useForm } from '@frontend/design-system';

export type ConenctProviderFormValues = {
  merchantNumber: string;
  validationMethod: string;
  validationCode: string;
};
export type ConnectProviderFormProps = {
  onSubmit: (values: ConenctProviderFormValues) => Promise<void>;
  onChangeFormState: (values: ConenctProviderFormValues, isComplete: boolean) => void;
};
export const ConnectProviderForm = ({ onSubmit, onChangeFormState }: ConnectProviderFormProps) => {
  const { t } = useTranslation('payments');

  const { values, isComplete, getFieldProps, validate } = useForm({
    fields: {
      merchantNumber: {
        type: 'text',
        required: true,
        minLength: 4,
      },
      validationMethod: {
        type: 'radio',
        required: true,
      },
      validationCode: {
        type: 'text',
        required: true,
        validator: (field, { validationMethod }) => {
          if (validationMethod.value === 'last4' && !/^\d{4}$/.test(field.value)) {
            return t('Last 4 digits must be a 4-digit number');
          }

          if (validationMethod.value === 'practice' && field.value.length < 5) {
            return t('Practice code must be at least 5 characters');
          }

          return '';
        },
      },
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isComplete) {
      validate();
      return;
    }

    onSubmit({
      merchantNumber: values.merchantNumber || '',
      validationMethod: values.validationMethod || '',
      validationCode: values.validationCode || '',
    });
  };

  const codeLabel = values.validationMethod === 'practice' ? t('Practice Code') : t('Last 4 Digits');

  useEffect(() => {
    onChangeFormState(
      {
        merchantNumber: values.merchantNumber || '',
        validationMethod: values.validationMethod || '',
        validationCode: values.validationCode || '',
      },
      isComplete
    );
  }, [values, isComplete]);

  return (
    <form className='connect-provider-form' onSubmit={handleSubmit}>
      <FormRow>
        <TextField label={t('Merchant ID Number')} {...getFieldProps('merchantNumber')} />
      </FormRow>
      <Text weight='bold' size='large' css={{ marginBottom: theme.spacing(1) }}>
        {t('Select Validation Method')}
      </Text>
      <FormRow>
        <RadioField {...getFieldProps('validationMethod')}>
          <RadioField.Radio value={'practice'}>{t('Practice Code')}</RadioField.Radio>
          <RadioField.Radio value={'last4'}>{t('Last 4 digits of bank account number')}</RadioField.Radio>
        </RadioField>
      </FormRow>
      <FormRow>
        <TextField label={codeLabel} {...getFieldProps('validationCode')} />
      </FormRow>
    </form>
  );
};
